import _ from 'lodash';
import d3Tip from 'd3-tip';
import dc, { BarChart, BaseMixin } from 'dc';
import $ from 'jquery';
import crossfilter, { Crossfilter, Dimension, Group } from 'crossfilter2';
import * as d3 from 'd3';

interface ChartData {
  dimension: Dimension<any, any>;
  group: Group<any, any, any>;
  stackNames: string[];
  stackSize: number;
  maxValue: number;
  valuesTotal: number;
}

interface SeriesItem {
  name: string;
  data: number[];
  altData?: number[];
}

interface DataItem {
  key: string;
  values: number[];
  altValues: number[];
  totalValue: number;
}

interface BaseMixinWithTippy extends BaseMixin<any> {
  tippy?: any;
}

const D3ChartService = {
  calculateBarWidth(chart: BarChart): number {
    const MAX_BAR_WIDTH = 50;
    const MIN_BAR_WIDTH = 2;

    const numberOfBars = chart.xUnitCount();
    let barWidth = Math.floor(
      (chart.xAxis.length - (numberOfBars - 1) * chart.gap()) /
        (numberOfBars + 1),
    );

    barWidth = Math.max(barWidth, MIN_BAR_WIDTH);
    barWidth = Math.min(barWidth, MAX_BAR_WIDTH);
    return barWidth;
  },

  replaceChartWatch(
    scope: any,
    chartId: string,
    watchName: string,
    watch: () => void,
  ): void {
    if (!scope.charts) {
      scope.charts = {};
    }

    if (!_.has(scope.charts, chartId)) {
      scope.charts[chartId] = {};
    }
    const chart = scope.charts[chartId];

    if (_.has(chart, watchName)) {
      const oldWatch = chart[watchName];
      oldWatch();
    }

    chart[watchName] = watch;
  },

  watchResizeEvents(
    chart: BaseMixin<any>,
    element: HTMLElement,
    resizeCallback: (chart: BaseMixin<any>, element: HTMLElement, width: number) => void,
  ): () => void {
    const handleResize = () => {
      const width = $(element).width() || 0;
      resizeCallback(chart, element, width);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  },

  resizeChart(
    chart: BaseMixin<any>,
    element: HTMLElement,
    newWidth: number,
  ): void {
    if (newWidth <= 0) {
      return;
    }

    chart.width(newWidth).transitionDuration(0);
    chart.render();
    chart.transitionDuration(750);
  },

  adjustBar(chart: BarChart): void {
    const barWidth = this.calculateBarWidth(chart);
    const stepLength = chart.xAxis.length / chart.xUnitCount();
    const leftMargin = stepLength / 2;
    const domain = chart.xAxis().scale().domain();

    chart
      .selectAll('.bar')
      .attr('width', barWidth)
      .attr('x', (d: any) => {
        const barIndex = domain.indexOf(d.x);
        const x = leftMargin + barIndex * stepLength - barWidth / 2;
        return dc.utils.safeNumber(x);
      })
      .on('click', null);
  },

  adjustBarLabels(chart: BarChart): void {
    const maxWidth = this.calculateBarWidth(chart) + chart.gap();

    chart
      .select('.axis.x')
      .selectAll('text')
      .each(function () {
        const text = d3.select(this as EventTarget);
        const words = text.text().split(/\s+/).reverse();
        let line: string[] = [];
        let lineNumber = 0;
        const lineHeight = 1.1;
        const y = text.attr('y');
        const dy = parseFloat(text.attr('dy'));
        let tspan = text
          .text('')
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('dy', `${dy}em`);

        let word;
        while ((word = words.pop())) {
          line.push(word);
          tspan.text(line.join(' '));

          if ((tspan.node() as SVGTextContentElement).getComputedTextLength() > maxWidth && line.length > 1) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            tspan = text
              .append('tspan')
              .attr('x', 0)
              .attr('y', y)
              .attr('dy', `${++lineNumber * lineHeight + dy}em`)
              .text(line.join(' '));
          }
        }
      });

    const stepLength = chart.xAxis().length / chart.xUnitCount();
    const leftMargin = stepLength / 2;
    const domain = chart.xAxis().scale().domain();
    chart
      .select('.axis.x')
      .selectAll('.tick')
      .attr('transform', (d: any) => {
        const barIndex = domain.indexOf(d);
        const x = leftMargin + barIndex * stepLength;
        return `translate(${dc.utils.safeNumber(x)},0)`;
      });
  },

  adjustLegend(chart: BaseMixin<any>): void {
    const sidePadding = 15;
    const gap = 10;

    let cumulativeWidth = 0;
    let row = 0;

    chart.selectAll('.dc-legend-item').attr('transform', function () {
      const x = dc.utils.safeNumber(cumulativeWidth);
      const y = dc.utils.safeNumber(row * (chart.legend().itemHeight() + gap));
      const itemWidth = (this as SVGGraphicsElement).getBBox().width + gap;

      if (cumulativeWidth + 2 * itemWidth + 2 * sidePadding >= chart.width()) {
        row++;
        cumulativeWidth = 0;
      } else {
        cumulativeWidth += itemWidth;
      }
      return `translate(${x},${y})`;
    });

    chart.select('.dc-legend').attr('transform', () => {
      const x = sidePadding;
      const y = chart.height() - (row + 1) * (chart.legend().itemHeight() + gap);
      return `translate(${x},${y})`;
    });
  },

  addTooltips(chart: BaseMixinWithTippy): void {
    chart
    if (chart.tippy) {
      chart.tippy.destroy();
      chart.tippy = null;
    }

    const elements = chart.selectAll('.bar, .pie-slice:not(.pie-label)');
    if (elements.empty()) {
      return;
    }

    const isBarChart = !chart.selectAll('.bar').empty();

    const tip = (<any>d3Tip)()
      .attr('class', isBarChart ? 'd3-tip d3-tip-arrow' : 'd3-tip')
      .offset([-10, 0])
      .html((d: any) => {
        const titleFunction = 'stack' in chart && typeof chart.stack === 'function' 
          ? chart.title(d.layer) 
          : chart.title();
        const data = d.data || d;
        return titleFunction(data);
      });

    elements
      .call(tip)
      .on('mouseover.tippy', tip.show)
      .on('mouseleave.tippy', tip.hide);

    elements.selectAll('title').remove();
    chart.tippy = tip;
  },

  buildChartData(series: SeriesItem[], categories: string[]): ChartData {
    const data: DataItem[] = categories.map((category, i) => {
      const values = series.map(item => item.data[i]);
      const altValues = series.map(item => item.altData ? item.altData[i] : item.data[i]);
      const totalValue = values.reduce((sum, value) => sum + value, 0);
      return { key: category, values, altValues, totalValue };
    });

    const stackNames = _.map(series, 'name');
    const itemTotals = _.map(data, 'totalValue');
    const maxValue = _.max(itemTotals) || 0;
    const valuesTotal = _.reduce(itemTotals, (sum, value) => sum + value, 0);
    const chartData = crossfilter(data);
    const dimension = chartData.dimension((item) => item.key);

    const group = dimension.group().reduce(
      (current, next) => {
        const currentitem = current as DataItem;
        for (let i = 0; i < currentitem?.values.length; i++) {
          currentitem.values[i] += next.values[i];
          currentitem.altValues[i] += next.altValues[i];
        }
        return currentitem;
      },
      (current, next) => {
        const currentitem = current as DataItem;
        for (let i = 0; i < currentitem.values.length; i++) {
          currentitem.values[i] += next.values[i];
          currentitem.altValues[i] += next.altValues[i];
        }
        return current;
      },
      () => ({
        values: _.map(stackNames, () => 0),
        altValues: _.map(stackNames, () => 0),
      }),
    );

    return {
      dimension,
      group,
      stackNames,
      stackSize: stackNames.length,
      maxValue,
      valuesTotal,
    };
  },

  percent(value: number, total: number): number {
    if (!total || total <= 0) {
      return 0;
    }
    return Math.round((value / total) * 100);
  },
};

export default D3ChartService;